/* Header navigation: "-" children become disclosure dropdowns.
   =====================================================================
   Ghost's navigation is a flat list. The admin convention (shared with the
   other Enova themes, documented for Miyus as "-- Label") is that a label
   starting with "-" belongs under the item before it. partials/navigation.hbs
   marks those items with the `nav-sub-item` class and removes the marker
   server-side; this module rebuilds the list into top-level links and, for
   each marked group, a disclosure: a <button aria-expanded aria-controls>
   plus a plain list whose first row is the parent's own link, so the parent
   page stays reachable even though the bar entry became a button.

   WAI-ARIA disclosure pattern, no menu roles. Click, Enter and Space toggle;
   ArrowDown opens and focuses the first row; Escape closes and returns focus
   to the button only when focus was inside the group; a click or a focus move
   outside closes; on pointer devices hover opens after a short intent delay
   and closes a little later. One group is open at a time.

   The same markup serves the mobile drawer (header.css positions the panel
   statically inside `.gh-head-open`), where the group holding the current
   page starts open so the reader lands on their own location.

   Progressive enhancement: before this runs, header.css hides the sub-items
   while the list carries `data-nav-pending` (and a 5-second keyframe
   releases them if the bundle never runs). Without scripts the flat list
   shows every link with its marker already removed by the partial.

   Compatibility: the group <li> keeps Ghost's `nav-<slug>` / `nav-current`
   classes and gains the 1.5.x `dropdown nav-wrapper` hooks, the panel is
   `ul.nav.nav-panel` and the chevron sits in `span.nav-trigger`, so CSS a
   site owner added through Code injection keeps matching. Label reads go
   through nav-label.js, so an icon (Ghost 6.59+) is never lost and an
   icon-only parent's button is named by the image's alt. */

import { iconNodeOf, labelNodeOf, stripMarkerInPlace } from './nav-label.js';

const HOVER_OPEN_DELAY = 120;
const HOVER_CLOSE_DELAY = 180;
const HOVER_MQ = '(hover: hover)';
const DRAWER_MQ = '(max-width: 900px)';
const CHEVRON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true" focusable="false"><path d="M201.4 342.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 274.7 86.6 137.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z"/></svg>';

/** Remove a whole leading run of dashes and spaces from a label. A label
 *  that would end up empty keeps its text: a link is never nameless. */
export function stripDashPrefix(label) {
  const value = String(label == null ? '' : label).trim();
  return value.replace(/^[\s-]+/, '').trim() || value;
}

/** Group the flat <li> items by the `nav-sub-item` marker. An orphan
 *  sub-item with no parent above it is promoted to a top-level leaf: a
 *  mis-ordered menu must never make a link unreachable. */
export function groupNavItems(list) {
  const items = Array.from(list.children).filter((el) => el.tagName === 'LI');
  return items.reduce((groups, li) => {
    if (li.classList.contains('nav-sub-item') && groups.length > 0) {
      groups[groups.length - 1].children.push(li);
    } else {
      groups.push({ topItem: li, children: [] });
    }
    return groups;
  }, []);
}

/** Turn one group into button + panel inside the parent's own <li>, which
 *  keeps every class Ghost gave it. The parent anchor moves into the panel
 *  as its first row (the original node, so aria-current and the icon come
 *  along); the children move in after it. */
function buildGroup(group, index) {
  const li = group.topItem;
  const anchor = li.querySelector(':scope > a');
  const panelId = `miyus-nav-${index}`;
  const current = !!(anchor && anchor.hasAttribute('aria-current')) || group.children.some((c) => c.querySelector('a[aria-current]'));

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'nav-link nav-submenu-toggle';
  button.setAttribute('aria-expanded', 'false');
  button.setAttribute('aria-controls', panelId);

  // Same shape as the anchor it replaces: [icon] [label] chevron. The label
  // span is emitted only when there is label text: for an icon-only parent
  // the image's alt names the button by itself.
  const icon = anchor ? iconNodeOf(anchor) : null;
  if (icon) button.appendChild(icon.cloneNode(true));
  const labelText = anchor ? stripDashPrefix(labelNodeOf(anchor).textContent) : '';
  if (labelText) {
    const label = document.createElement('span');
    label.className = 'nav-label';
    label.textContent = labelText;
    button.appendChild(label);
  }
  const chevron = document.createElement('span');
  chevron.className = 'nav-trigger';
  chevron.setAttribute('aria-hidden', 'true');
  chevron.innerHTML = CHEVRON;
  button.appendChild(chevron);
  if (current) button.classList.add('is-active');

  const panel = document.createElement('ul');
  panel.id = panelId;
  panel.className = 'nav nav-panel';
  panel.hidden = true;
  if (anchor) {
    const row = document.createElement('li');
    row.className = 'nav-panel-parent';
    row.appendChild(anchor);
    panel.appendChild(row);
  }
  for (const child of group.children) {
    child.classList.remove('nav-sub-item');
    child.classList.add('nav-panel-item');
    panel.appendChild(child);
  }

  li.classList.add('dropdown', 'nav-wrapper');
  li.setAttribute('data-nav-group', '');
  li.appendChild(button);
  li.appendChild(panel);
  return { li, button, panel };
}

export default function initHeaderNav({ root = document } = {}) {
  const list = root.querySelector('[data-nav-list]');
  if (!list) return null;

  // Exactly once per document: the rebuild reads the flat markup it replaces.
  if (list.dataset.navBuilt) return null;
  list.dataset.navBuilt = '1';

  try {
    // A customised partial may still carry raw markers; the stock one has
    // removed them already, so this is a no-op there. Only the label span is
    // rewritten: a textContent write on the anchor would delete its icon.
    list.querySelectorAll(':scope > li.nav-sub-item > a').forEach((a) => stripMarkerInPlace(a, stripDashPrefix));

    const groups = groupNavItems(list);
    if (!groups.length) return null;

    const triggers = [];
    groups.forEach((group, index) => {
      if (!group.children.length) {
        // A promoted orphan is a top item now, not an indented row.
        if (group.topItem.classList.contains('nav-sub-item')) {
          group.topItem.classList.remove('nav-sub-item');
          group.topItem.classList.add('nav-top-item');
        }
        return;
      }
      triggers.push(buildGroup(group, index));
    });
    if (!triggers.length) return null;

    const hoverMq = window.matchMedia(HOVER_MQ);
    const drawerMq = window.matchMedia(DRAWER_MQ);
    const hoverAllowed = () => hoverMq.matches && !drawerMq.matches;

    /* Single-open disclosure state machine. */
    let openTrigger = null;
    const close = (t) => {
      t.panel.hidden = true;
      t.button.setAttribute('aria-expanded', 'false');
      if (openTrigger === t) openTrigger = null;
    };
    const open = (t) => {
      if (openTrigger && openTrigger !== t) close(openTrigger);
      t.panel.hidden = false;
      t.button.setAttribute('aria-expanded', 'true');
      openTrigger = t;
    };
    const closeAll = () => {
      if (openTrigger) close(openTrigger);
    };

    const timers = [];
    for (const t of triggers) {
      let openTimer = 0;
      let closeTimer = 0;
      const cancel = () => {
        clearTimeout(openTimer);
        clearTimeout(closeTimer);
        openTimer = closeTimer = 0;
      };
      timers.push(cancel);

      t.button.addEventListener('click', () => {
        cancel();
        if (openTrigger === t) close(t);
        else open(t);
      });

      // ArrowDown from the button drops into the panel, per the disclosure
      // navigation pattern. Escape is handled once, at the document.
      t.button.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        cancel();
        open(t);
        const first = t.panel.querySelector('a');
        if (first) first.focus();
      });

      // Hover intent on pointer devices only, and never inside the drawer: a
      // short delay in, a slightly longer one out, so a pointer crossing the
      // bar does not flicker panels open and shut.
      t.li.addEventListener('mouseenter', () => {
        if (!hoverAllowed()) return;
        clearTimeout(closeTimer);
        closeTimer = 0;
        if (openTrigger === t) return;
        openTimer = setTimeout(() => {
          openTimer = 0;
          open(t);
        }, HOVER_OPEN_DELAY);
      });
      t.li.addEventListener('mouseleave', () => {
        if (!hoverAllowed()) return;
        clearTimeout(openTimer);
        openTimer = 0;
        if (openTrigger !== t) return;
        closeTimer = setTimeout(() => {
          closeTimer = 0;
          close(t);
        }, HOVER_CLOSE_DELAY);
      });
    }

    const onDocumentClick = (event) => {
      if (openTrigger && !openTrigger.li.contains(event.target)) closeAll();
    };
    // Escape closes wherever focus is; focus moves to the button ONLY when it
    // was inside the open group, so a reader elsewhere keeps their place.
    const onDocumentKeydown = (event) => {
      if (event.key !== 'Escape' || !openTrigger) return;
      const t = openTrigger;
      const inside = t.li.contains(document.activeElement);
      closeAll();
      if (inside) t.button.focus();
    };
    // Tabbing out of an open panel must not leave it floating behind the page.
    const onDocumentFocusin = (event) => {
      if (openTrigger && !openTrigger.li.contains(event.target)) closeAll();
    };
    // Scrolling closes a hover-opened flyout, but not one the keyboard is
    // inside (focusing a row below the fold scrolls the page), and never in
    // the drawer, where the panels are part of the scrolling column.
    const onScroll = () => {
      if (!openTrigger || drawerMq.matches) return;
      if (openTrigger.li.contains(document.activeElement)) return;
      closeAll();
    };

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKeydown);
    document.addEventListener('focusin', onDocumentFocusin);
    window.addEventListener('scroll', onScroll, { passive: true });
    // The page may go into the back/forward cache: never restore it with a panel open.
    window.addEventListener('pagehide', closeAll);

    // In the drawer, land the reader on their own section.
    if (drawerMq.matches) {
      const active = triggers.find((t) => t.button.classList.contains('is-active'));
      if (active) open(active);
    }

    return () => {
      closeAll();
      for (const cancel of timers) cancel();
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onDocumentKeydown);
      document.removeEventListener('focusin', onDocumentFocusin);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', closeAll);
    };
  } finally {
    // Every early return and throw above lands here: the pending guard must
    // never stay armed, or the sub-items stay hidden with no way to reach them.
    list.removeAttribute('data-nav-pending');
  }
}
